/**
 * Analytics utility.
 * Tracks events to console in development;
 * integrates with any analytics provider in production.
 */

const isDev = process.env.NODE_ENV === 'development';

/**
 * Track a named event with optional properties.
 * @param {string} name - Event name
 * @param {object} props - Event properties
 */
export function trackEvent(name, props = {}) {
  if (isDev) {
    console.log(`[Analytics] ${name}`, props);
    return;
  }

  // Integrate with your analytics provider here:
  // e.g., window.gtag?.('event', name, props);
  // e.g., window.analytics?.track(name, props);
  // e.g., window.posthog?.capture(name, props);
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', name, props);
    }
  } catch (err) {
    // Silently fail — analytics should never break UX
  }
}

/**
 * Initialize scroll depth tracking.
 * Fires events at 25%, 50%, 75%, 100% scroll depth.
 */
export function initScrollDepthTracking() {
  if (typeof window === 'undefined') return () => {};

  const depths = [25, 50, 75, 100];
  const fired = new Set();

  const handleScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight =
      document.documentElement.scrollHeight - document.documentElement.clientHeight;
    if (docHeight <= 0) return;
    const pct = Math.round((scrollTop / docHeight) * 100);

    depths.forEach((depth) => {
      if (pct >= depth && !fired.has(depth)) {
        fired.add(depth);
        trackEvent('scroll_depth', { depth: `${depth}%` });
      }
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  return () => window.removeEventListener('scroll', handleScroll);
}